import React, { useCallback, useMemo } from 'react';
import { StackScreenProps } from '@react-navigation/stack';

import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { DESKTOP_CONFIRM_COPY } from '@/constants/admin';
import { useEditResourceMutation, useDeleteResourceMutation, convertToFormData } from '@/store/api/admin/resourcesApi';
import { ensureWebFileParts } from '@/utils/admin/files';
import { IFile, IResourceFormValues } from '@/types/admin/resources';
import { useAdminUser } from '@/hooks/admin/useAdminUser';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useEditResourceForm } from '@/components/admin/resources/CreateResource/hooks/useEditResourceForm';
import { showAlert, showConfirm } from '@/utils/common/alert';
import {
  ResourceDetailsSection,
  ResourceContentSection,
  ResourceFilesSection,
} from '@/components/admin/resources/CreateResource/components';
import { MobileFormScreen } from '@/components/common/admin/mobile/MobileFormScreen';

type EditResourceScreenProps = StackScreenProps<AdminStackParamList, 'EditResource'>;

export const EditResourceScreen: React.FC<EditResourceScreenProps> = ({
  navigation,
  route,
}) => {
  const { resource } = route.params;
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;

  const [editResource, { isLoading: isEditing }] = useEditResourceMutation();
  const [deleteResource, { isLoading: isDeleting }] = useDeleteResourceMutation();

  // Custom hook for form logic
  const {
    formData,
    errors,
    validateForm,
    handleFieldChange,
  } = useEditResourceForm(resource);

  // Form submission handler
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!selectedPropertyId) {
      showAlert('Error', 'No property selected');
      return;
    }

    try {
      // Normalize files to IFile[] and ensure web parts for compatibility
      let normalizedFiles: IFile[] = (formData.files || []).map((f: any) => ({
        id: f?.id,
        name: f?.name,
        size: f?.size ?? 0,
        type: f?.type,
        uri: f?.uri,
        file: f?.file,
      }));
      normalizedFiles = await ensureWebFileParts(normalizedFiles);

      const adjusted: IResourceFormValues = {
        ...formData,
        files: normalizedFiles,
      } as unknown as IResourceFormValues;

      // Convert form data to FormData for API
      const resourceFormData = convertToFormData(adjusted, selectedPropertyId);
      // Add the resource ID for editing
      resourceFormData.append('id', resource.id.toString());

      const result = await editResource(resourceFormData).unwrap();

      if (result?.success !== false) {
        showSuccess('Resource updated successfully!');
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Edit resource error:', error);
      showApiError(error, 'Failed to update resource');
    }
  }, [formData, validateForm, selectedPropertyId, editResource, resource.id, showSuccess, showApiError, navigation]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    const confirmed = await showConfirm(
      'Delete Resource',
      `Are you sure you want to delete "${resource.name}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      const result = await deleteResource(resource).unwrap();
      if (result?.success !== false) {
        showSuccess('Resource deleted successfully!');
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Delete resource error:', error);
      showApiError(error, 'Failed to delete resource');
    }
  }, [resource, deleteResource, showSuccess, showApiError, navigation]);

  // Double-confirm discard on cancel
  const confirmDiscardTwice = useCallback(async (onConfirm: () => void) => {
    const firstConfirm = await showConfirm(
      DESKTOP_CONFIRM_COPY.discardTitle,
      DESKTOP_CONFIRM_COPY.discardMessage,
      DESKTOP_CONFIRM_COPY.discardConfirmText,
      DESKTOP_CONFIRM_COPY.discardCancelText
    );

    if (firstConfirm) {
      onConfirm();
    }
  }, []);

  const handleCancel = useCallback(() => {
    confirmDiscardTwice(() => navigation.goBack());
  }, [confirmDiscardTwice, navigation]);

  // Memoized scroll content to prevent unnecessary re-renders
  const scrollContent = useMemo(() => (
    <>
      <ResourceDetailsSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <ResourceContentSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />

      <ResourceFilesSection
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
      />
    </>
  ), [
    formData,
    errors,
    handleFieldChange,
  ]);

  const isLoading = isEditing || isDeleting;

  return (
    <MobileFormScreen
      screenName="EditResource"
      navigation={navigation}
      isSubmitting={isLoading}
      submitText="Update"
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      showDelete
      onDelete={handleDelete}
    >
      {scrollContent}
    </MobileFormScreen>
  );
};
